import { Grid, withStyles } from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import _, { find } from 'lodash';
import React, { useContext, useRef, useState } from 'react';
import {
  fetchEnd,
  fetchStart,
  Query,
  ReferenceArrayInput,
  refreshView,
  SaveButton,
  useNotify
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import { connect } from 'react-redux';

import UserRolesApi from '../../../core/apis/UserRolesApi';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import GridInputFilter from '../../../core/components/inputs/GridInputFilter';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { CreationContactContext } from '../creationsteps/CreationContactContext';
import GridInput from '../../../core/components/inputs/GridInput';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const styles = () => ({
  fullWidth: {
    width: '100%'
  }
});

const validateRoles = (value, _allValues, _props, _field) => {
  if (Array.isArray(value) && value.length < 1) {
    return 'At least one Role must be selected';
  }
  return undefined;
};

const AddRolesDialog = props => {
  const {
    creation,
    onSubmitEnd,
    setAccessRoles,
    accessRoles,
    setSelectedAccessRoles
  } = props;
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const context = useContext(CreationContactContext);
  let formProps = {};
  const rolesToDelete = useRef([]);
  const formRef = useRef();

  const handleAddClick = event => {
    formProps.form.submit(event);
  };

  const handleClose = () => {
    const { onClose } = props;
    if (submitting) return;
    formProps.form.reset();
    onClose();
  };

  const handleSubmit = values => {
    const { fetchStart, fetchEnd, onClose, userId, refreshView } = props;

    // Userroles to create
    const userRoles = values.roles.map(role => ({
      userId: userId,
      roleId: role
    }));

    // Dispatch an action letting react-admin know a API call is ongoing
    fetchStart();
    setSubmitting(true);

    // Call API to delete roles unchecked (only if we are creating a user).
    if (creation) {
      if (rolesToDelete.current.length > 0) {
        UserRolesApi.delete(
          rolesToDelete.current.map(role => ({
            userId: userId,
            roleId: role
          }))
        )
          .then(() => formProps.form.reset())
          .catch(({ response }) => {
            notifyServerError(response, undefined);
            formProps.form.reset();
          });
      }
    }

    // Call API to create user/roles links
    UserRolesApi.create(userRoles)
      .then(res => {
        if (!creation) {
          setSubmitting(false);
          onClose();
          refreshView();
          notify('ra.notification.added', {
            messageArgs: { smart_count: userRoles.length }
          });
          formProps.form.reset();
        }
        // Dispatch an action letting react-admin know a API call has ended
        if (creation) {
          setAccessRoles({ roles: res.map(item => item.roleId) });
          onSubmitEnd();
        }
        fetchEnd();
      })
      .catch(({ response }) => {
        if (!creation) {
          onClose();
        }
        notifyServerError(response);
        formProps.form.reset();
        fetchEnd();
      });
  };

  const { open, userId } = props;

  if (!creation) {
    return (
      <Dialog
        fullWidth
        open={open}
        aria-label="Add Roles"
        maxWidth="sm"
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return null;
          handleClose();
        }}
      >
        <DialogTitleWithCloseButton onClose={handleClose}>
          Add Access Role
        </DialogTitleWithCloseButton>
        <DialogContent>
          <SimpleFormWithSave
            toolbar={null}
            resource="roles"
            onSave={handleSubmit}
            variant="standard"
            margin="normal"
          >
            <FormSpy>
              {props => {
                formProps = props;
                return (
                  <Query
                    type="GET_MANY_REFERENCE"
                    resource="userroles"
                    payload={{
                      target: 'userId',
                      id: userId,
                      sort: {}
                    }}
                  >
                    {({ data, total, loading, error }) => {
                      if (!loading && !error) {
                        const filter = choices => {
                          return choices.filter(
                            item =>
                              find(data, ['roleId', item.id]) === undefined
                          );
                        };

                        return (
                          <Grid container spacing={1}>
                            <Grid item sm={12}>
                              <ReferenceArrayInput
                                label="Select one or multiple items from the list below."
                                optionTextLabel="Access Role Name"
                                optionValueLabel="Access Role ID"
                                resource="roles"
                                reference="roles"
                                source="roles"
                                perPage={-1}
                                validate={[validateRoles]}
                              >
                                <GridInputFilter
                                  optionValue="id"
                                  optionText="description"
                                  filter={filter}
                                />
                              </ReferenceArrayInput>
                            </Grid>
                          </Grid>
                        );
                      } else {
                        return <></>;
                      }
                    }}
                  </Query>
                );
              }}
            </FormSpy>
          </SimpleFormWithSave>
        </DialogContent>
        <DialogActions>
          <SaveButton
            saving={submitting}
            handleSubmitWithRedirect={handleAddClick}
            label="Add"
            icon={<></>}
            id="add"
          />
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            disabled={submitting}
            id={'cancel'}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  } else {
    return (
      <CustomSimpleForm
        toolbar={null}
        resource="roles"
        save={handleSubmit}
        variant="standard"
        margin="normal"
        initialValues={accessRoles}
      >
        <FormSpy>
          {props => {
            formProps = props;
            formRef.current = props.form;
            setTimeout(() => {
              setSelectedAccessRoles(props.values);
            }, 1);
            // In order to know what role(s) has to be deleted, we look at the difference between what the user already has and what has been checked.
            rolesToDelete.current = _.differenceWith(
              accessRoles.roles,
              formProps.values.roles,
              !_.isEqual
            );

            if (context.contactContext.currentForm === undefined) {
              context.setContactContext({
                ...context.contactContext,
                currentForm: formProps.form
              });
            }
            return (
              <Query
                type="GET_MANY_REFERENCE"
                resource="userroles"
                payload={{
                  target: 'userId',
                  id: userId,
                  sort: {}
                }}
              >
                {({ _data, _total, loading, error }) => {
                  if (!loading && !error) {
                    return (
                      <Grid container spacing={1}>
                        <Grid item sm={12}>
                          <ReferenceArrayInput
                            optionTextLabel="Access Role Name"
                            optionValueLabel="Access Role ID"
                            resource="roles"
                            reference="roles"
                            source="roles"
                            label={null}
                            perPage={-1}
                          >
                            <GridInput
                              optionValue="id"
                              optionText="description"
                            />
                          </ReferenceArrayInput>
                        </Grid>
                      </Grid>
                    );
                  } else {
                    return null;
                  }
                }}
              </Query>
            );
          }}
        </FormSpy>
      </CustomSimpleForm>
    );
  }
};

const mapDispatchToProps = {
  fetchEnd,
  fetchStart,
  refreshView
};

export default connect(
  null,
  mapDispatchToProps
)(withStyles(styles)(AddRolesDialog));
